import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Modal from 'Component/Modal';
import Avaliacao from 'Interface/Avaliacoes';
import Matricula from 'Interface/Matriculas';
import {
  useEditarMatriculaMutation,
  useRecuperarAvaliacoesQuery,
} from 'Service';

interface Props {
  dados: Matricula;
}

const Lista = ({ dados }: Props): React.ReactElement => {
  const toast = useToast();
  const navegacao = useNavigate();
  const [modal, setModal] = useState<boolean>(false);
  const [modal2, setModal2] = useState<boolean>(false);
  const [editarMatricula] = useEditarMatriculaMutation();

  const {
    data: { avaliacoes: [avaliacao] } = { avaliacoes: [{} as Avaliacao] },
    refetch: rebuscarAvaliacao,
  } = useRecuperarAvaliacoesQuery(
    {
      finder: 'proximas',
      options: { curso_id: dados.curso_id },
    },
    {
      skip:
        dados.curso?.total_aulas != dados.aulas_concluidas ||
        dados.concluido == 1,
    }
  );

  const {
    data: { avaliacoes: [proximaAvaliacao] } = {
      avaliacoes: [{} as Avaliacao],
    },
    refetch: rebuscarProximaAvaliacao,
  } = useRecuperarAvaliacoesQuery(
    {
      finder: 'proximas',
      options: { curso_id: dados.curso_id, bloqueios: 0 },
    },
    {
      skip: !modal,
    }
  );

  const remover = async (): Promise<void> => {
    try {
      await editarMatricula({
        id: dados.id,
        arquivado: 1,
      }).unwrap();
    } catch (erro) {
      toast({
        titulo: 'Erro ao remover',
        conteudo: 'Verifique o registro de log para mais detalhes!',
        tema: 'danger',
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(rebuscarAvaliacao, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(rebuscarProximaAvaliacao, []);

  return (
    <React.Fragment>
      <div className="bg-dark clip-rounded-3 mb-4">
        <div className="row">
          <div className="col-md-4">
            <Link
              to={
                dados.proxima_aula_id
                  ? `/estudar/curso/${dados.curso?.slug}/aula/${dados.proxima_aula_id}`
                  : `/estudar/curso/${dados.curso?.slug}`
              }
            >
              <Imagem
                src={dados.curso?.imagem}
                className="w-100 hf-250 object-fit-cover"
              />
            </Link>
          </div>
          <div className="col-md-8">
            <div className="p-3 d-flex flex-column justify-content-between h-100">
              <Link
                to={
                  dados.proxima_aula_id
                    ? `/estudar/curso/${dados.curso?.slug}/aula/${dados.proxima_aula_id}`
                    : `/estudar/curso/${dados.curso?.slug}`
                }
                className="text-decoration-none"
              >
                <div className="text-center text-sm-start">
                  {dados.proxima_aula && (
                    <p className="text-white mb-0 fw-light">
                      <span className="fw-bold text-muted">Próxima aula:</span>{' '}
                      {dados.proxima_aula?.titulo}
                    </p>
                  )}
                  <h3 className="text-white fw-light">{dados.curso?.nome}</h3>
                  <span className="d-inline-block px-1 bg-secondary rounded text-body fs-6 fw-bold">
                    {dados.curso?.categoria?.nome}
                  </span>
                  {dados.concluido ? (
                    <span className="d-inline-block px-1 bg-success rounded text-body fs-6 fw-bold ms-2">
                      <Icone nome="check-circle-fill" className="me-2" />
                      Concluído
                    </span>
                  ) : null}
                </div>
              </Link>
              <div className="row align-items-end">
                <div className="col-md-6 d-flex justify-content-center justify-content-sm-start mt-3 mt-sm-0">
                  <p className="fs-2 fw-lighter mb-0 text-white d-inline-block">
                    {dados.aulas_concluidas} / {dados.curso?.total_aulas}
                    <small className="fs-6 d-block text-muted">
                      Aulas feitas
                    </small>
                  </p>
                  <p className="fs-2 fw-lighter mb-0 text-white d-inline-block ms-4">
                    {dados.curso?.carga_horaria}hrs
                    <small className="fs-6 d-block text-muted">
                      Carga horária
                    </small>
                  </p>
                </div>
                <div className="col-md-6 d-flex justify-content-center justify-content-sm-end mt-3 mt-sm-0 gap-2">
                  {dados.concluido && !dados.certificado_emitido && (
                    <BotaoLink
                      lg
                      to={`/estudar/curso/${dados.curso?.slug}/certificado`}
                      icone="cloud-download"
                      tema="outline-primary"
                    >
                      Solicitar certificado
                    </BotaoLink>
                  )}
                  {dados.certificado?.url && (
                    <BotaoLink
                      lg
                      to={dados.certificado.url}
                      icone="cloud-download"
                      tema="outline-success"
                      target="_blank"
                    >
                      Download do certificado
                    </BotaoLink>
                  )}
                  {!dados.concluido &&
                    dados.curso?.total_aulas == dados.aulas_concluidas && (
                      <React.Fragment>
                        {avaliacao ? (
                          <Botao
                            lg
                            icone="play-circle"
                            tema="outline-secondary"
                            onClick={() =>
                              navegacao(
                                `/estudar/curso/${dados.curso?.slug}/avaliacao/processar`
                              )
                            }
                            confirmacao="Tem certeza que deseja realizar a avaliação agora?"
                            texto="Iniciar avaliação"
                          />
                        ) : (
                          <Botao
                            lg
                            tema="outline-secondary"
                            onClick={() => setModal(true)}
                            texto="Quando é a prova?"
                          />
                        )}
                      </React.Fragment>
                    )}
                  {!dados.concluido &&
                    dados.curso?.total_aulas != dados.aulas_concluidas && (
                      <Botao
                        lg
                        tema="outline-secondary"
                        onClick={() => setModal2(true)}
                        texto="Quando é a prova?"
                      />
                    )}
                  {!dados.concluido && (
                    <Botao
                      lg
                      icone="trash"
                      tema="danger"
                      onClick={remover}
                      confirmacao="Tem certeza que deseja remover esse curso da sua lista?"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {dados.curso?.total_aulas && (
          <div
            className="progress"
            style={{ backgroundColor: 'rgba(0,0,0, 0.2)', height: '5px' }}
          >
            <div
              className="progress-bar bg-success"
              style={{
                width: `${
                  (dados.aulas_concluidas * 100) / dados.curso.total_aulas
                }%`,
              }}
            ></div>
          </div>
        )}
      </div>
      <Modal exibir={modal2} aoFechar={setModal2}>
        <p>
          Para que sua prova possa ser liberada, você deverá concluir todas as
          aulas do curso. Após a conclusão, verificaremos se você não
          ultrapassou o limite máximo de carga horária diária.
        </p>
        <p>
          Se tudo estiver correto, sua prova será liberada! Caso contrário, lhe
          informaremos o prazo de liberação.
        </p>
        <div className="d-flex justify-content-end">
          <Botao onClick={() => setModal2(false)} texto="Entendi" />
        </div>
      </Modal>
      <Modal exibir={modal} aoFechar={setModal}>
        {proximaAvaliacao ? (
          <p>
            A data de liberação da prova do seu próximo módulo é{' '}
            <b>
              {DateTime.now()
                .plus({ days: proximaAvaliacao.prazo })
                .toLocaleString()}
            </b>{' '}
            caso você não ultrapasse o limite de carga horária diário para
            estudo.
          </p>
        ) : (
          <p>A avaliação do curso ainda não foi configurada.</p>
        )}
        <div className="d-flex justify-content-end">
          <Botao onClick={() => setModal(false)} texto="Entendi" />
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default Lista;
